import React, { useState, useEffect, useRef } from 'react';
import {
  Container, WysiwygField,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  CoreLink,
} from '@uncinc/react-drupal-core';
import { ParagraphDossierContent as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

export interface ParagraphDossierContentProps extends React.HTMLProps<HTMLDivElement> {
  entity: {
    toJS: () => Type;
  };
  dossierId?: string | number;
}

const ParagraphDossierContent: React.FC<ParagraphDossierContentProps> = ({ entity, dossierId }) => {
  const { t } = useTranslation();
  const paragraph = entity.toJS();
  const title = paragraph.field_title?.[0]?.value;
  const body = paragraph.field_body?.[0]?.processed ?? '';
  const items = paragraph.field_dossier_content_item ?? [];

  const classNames = c('Paragraph', 'ParagraphDossierContent');
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const extraContentRef = useRef<HTMLDivElement>(null);
  const hasMoreItems = items.length > 6;
  const hiddenItems = items.slice(6);

  useEffect(() => {
    if (extraContentRef.current) {
      setContentHeight(extraContentRef.current.scrollHeight);
    }
  }, [hiddenItems]);

  const getContentType = (nodeUrl?: string, hasContentDossier?: boolean): string => {
    if (!hasContentDossier) {
      return t('dossier.content_types.external_link');
    }

    if (nodeUrl?.startsWith('/kennisaanbod/onderzoek/')) {
      return t('dossier.content_types.research');
    }

    return t('dossier.content_types.article');
  };

  const renderItem = (item: any) => {
    const parsedItem = item.entity;
    const postTitle = parsedItem.field_title?.[0]?.value;
    const link = parsedItem.field_link?.[0]?.uri;
    const nodeUrl = parsedItem.field_content_dossier?.[0]?.url;
    const hasContentDossier = !!parsedItem.field_content_dossier?.[0]?.target_type;
    const subTitle = getContentType(nodeUrl, hasContentDossier);

    return (
      <CoreLink key={postTitle} href={`${nodeUrl ?? link}?dossier_id=${dossierId}`}>
        <div className="TaxonomySubject__relevant__button">
          <div className="TaxonomySubject__relevant__button_text">
            <p>{t(subTitle)}</p>
            <h3 className="asH5">{postTitle}</h3>
          </div>
          <i className={`icon icon-arrow-right ${!hasContentDossier && 'external-link'}`} />
        </div>
      </CoreLink>
    );
  };

  return (
    <div className={classNames}>
      <Container>
        <div className="ParagraphDossierContent__wrapper">
          <div className="row">
            <div className="col-12">
              <h2>{title}</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-lg-8">
              <WysiwygField property="schema:text" html={body} />
            </div>
          </div>
          <div className="ParagraphDossierContent__items">
            {items.slice(0, 6).map(renderItem)}

            {hasMoreItems && (
              <div
                className={c('ParagraphDossierContent__items-extra', { 'ParagraphDossierContent__items-extra--expanded': expanded })}
                style={{ height: expanded ? `${contentHeight}px` : '0px' }}
              >
                <div ref={extraContentRef} className="ParagraphDossierContent__items-extra-content">
                  {hiddenItems.map(renderItem)}
                </div>
              </div>
            )}
          </div>

          {hasMoreItems && (
            <Button onClick={() => setExpanded(!expanded)} className="Button--ghost">
              {expanded ? t('dossier.show_less') : t('dossier.show_more')}
              <i className={c('icon icon-arrow-right', { 'icon--rotated': expanded })} />
            </Button>
          )}
        </div>
      </Container>
    </div>
  );
};


export default ParagraphDossierContent;
